import { ChevronLeft, ChevronRight, Info, LayoutGrid, List, Loader2, Wifi, WifiOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarSwitcher, type SwitchableCalendar } from "@/components/schedule/CalendarSwitcher";
import { ScheduleViewSwitcher } from "@/components/schedule/ScheduleViewSwitcher";
import { ScheduleDateNavigation } from "@/components/schedule/ScheduleDateNavigation";

export type AthleteScheduleViewMode = "day" | "week";

interface AthleteScheduleToolbarProps {
  calendars: SwitchableCalendar[];
  selectedCalendarId: string | null;
  onCalendarSelect: (id: string | null) => void;
  viewMode: AthleteScheduleViewMode;
  onViewModeChange: (mode: AthleteScheduleViewMode) => void;
  dateLabel: string;
  connectionStatus: string;
  bookingAdvanceDays: number;
  canGoBack: boolean;
  onPrevious: () => void;
  onNext: () => void;
}

export function AthleteScheduleToolbar({
  calendars,
  selectedCalendarId,
  onCalendarSelect,
  viewMode,
  onViewModeChange,
  dateLabel,
  connectionStatus,
  bookingAdvanceDays,
  canGoBack,
  onPrevious,
  onNext,
}: AthleteScheduleToolbarProps) {
  const { t } = useTranslation(["athlete", "common"]);
  const views = [
    { value: "week" as const, icon: LayoutGrid, label: t("schedule.weekView", { defaultValue: "Semaine" }) },
    { value: "day" as const, icon: List, label: t("schedule.dayView", { defaultValue: "Jour" }) },
  ];

  return (
    <div className="athlete-schedule-toolbar">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        {calendars.length > 1 ? (
          <CalendarSwitcher
            calendars={calendars}
            selectedCalendarId={selectedCalendarId}
            onSelect={onCalendarSelect}
            allLabel={t("schedule.allCalendars", { defaultValue: "Tous" })}
            ariaLabel={t("schedule.calendarFilter", { defaultValue: "Choisir un planning" })}
          />
        ) : <span />}

        <ScheduleViewSwitcher value={viewMode} onChange={onViewModeChange}
          options={views} label={t("schedule.viewMode", { defaultValue: "Affichage" })} />
      </div>

      <div className="mb-3">
      <ScheduleDateNavigation label={dateLabel} canGoBack={canGoBack} onPrevious={onPrevious} onNext={onNext}
        indicator={<Badge variant="outline" className="h-5 shrink-0 px-1.5" aria-label={connectionStatus}>
            {connectionStatus === "connected" ? <Wifi className="h-3 w-3" /> : connectionStatus === "connecting" ? <Loader2 className="h-3 w-3 animate-spin" /> : <WifiOff className="h-3 w-3" />}
          </Badge>}
        extra={bookingAdvanceDays > 0 && (
          <Popover>
            <PopoverTrigger asChild>
              <Button type="button" variant="outline" size="icon" aria-label={t("errors.bookingLimitInfo", { days: bookingAdvanceDays })}>
                <Info className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-64 text-sm leading-relaxed">
              {t("errors.bookingLimitInfo", { days: bookingAdvanceDays })}
            </PopoverContent>
          </Popover>
        )} />
      </div>
    </div>
  );
}